import React, { useState } from "react";
import styled from "styled-components";
import { Container, Row, Col } from "react-bootstrap";
import { useAuth, hasRole } from "../../context/AuthContext";

const SectionStyled = styled.section`
  padding: 96px 0 48px;
  background: var(--nd-black);
`;

const SectionLabel = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 36px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin-bottom: 8px;
`;

const SectionDesc = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  max-width: 550px;
  margin-bottom: 32px;
`;

const ProgressBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
`;

const ProgressTrack = styled.div`
  flex: 1;
  max-width: 200px;
  height: 4px;
  background: var(--nd-border-visible);
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: var(--nd-success);
  width: ${({ pct }) => pct}%;
  transition: width 300ms ease;
`;

const ProgressText = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

const AuthBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
  padding: 12px 0;
  border-bottom: 1px solid var(--nd-border);
`;

const AuthLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

const AuthBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 6px 16px;
  border-radius: 999px;
  border: 1px solid var(--nd-border-visible);
  background: transparent;
  color: var(--nd-text-primary);
  cursor: pointer;
  outline: none;
  &:hover { border-color: var(--nd-text-primary); }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  min-width: 70px;
`;

const Pill = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 4px;
  border: 1px solid ${({ active }) => active ? "var(--nd-text-display)" : "var(--nd-border-visible)"};
  background: ${({ active }) => active ? "var(--nd-text-display)" : "transparent"};
  color: ${({ active }) => active ? "var(--nd-black)" : "var(--nd-text-disabled)"};
  cursor: pointer;
  outline: none;
  transition: all 200ms ease;
`;

const ChallengeCard = styled.button`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  transition: border-color 250ms ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  text-align: left;
  cursor: pointer;
  outline: none;
  width: 100%;
  &:hover { border-color: var(--nd-border-visible); }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  width: 100%;
`;

const DiffBadge = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${({ level }) =>
    level === "easy" ? "rgba(74,158,92,0.15)" :
    level === "medium" ? "rgba(212,168,67,0.15)" :
    "rgba(215,25,33,0.15)"};
  color: ${({ level }) =>
    level === "easy" ? "var(--nd-success)" :
    level === "medium" ? "var(--nd-warning)" :
    "var(--nd-accent)"};
`;

const TimeBadge = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  color: var(--nd-text-disabled);
`;

const LockedTag = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid var(--nd-border-visible);
  color: var(--nd-text-disabled);
  margin-left: 8px;
`;

const CardTitle = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: var(--nd-text-display);
  margin-bottom: 8px;
  line-height: 1.3;
`;

const CardDesc = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  margin-bottom: 16px;
  flex-grow: 1;
`;

const Tags = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const Tag = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  color: var(--nd-text-disabled);
  background: rgba(255,255,255,0.03);
  padding: 2px 8px;
  border-radius: 4px;
`;

const CompletedDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--nd-success);
  display: inline-block;
  margin-left: 8px;
`;

const BottomCTA = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: var(--nd-text-secondary);
  text-align: center;
  margin-top: 48px;
  font-style: italic;
`;

const categories = ["all", "linux", "containers", "kubernetes", "cicd", "iac", "observability"];
const difficulties = ["all", "easy", "medium", "hard"];

const Labs = ({ labs = [], completedLabs = [], user, onSelectLab, instructions }) => {
  const { role, signIn } = useAuth();
  const [category, setCategory] = useState("all");
  const [difficulty, setDifficulty] = useState("all");

  const filtered = labs.filter(c => {
    if (category !== "all" && c.category !== category) return false;
    if (difficulty !== "all" && c.difficulty !== difficulty) return false;
    return true;
  });

  const completedCount = completedLabs.length;
  const totalCount = labs.length;
  const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <SectionStyled>
      <Container>
        <SectionLabel>DEVOPS LABS</SectionLabel>
        <SectionTitle>Learn by breaking things</SectionTitle>
        <SectionDesc>
          Challenges that teach DevOps the way we learned it — by building
          something, watching it fail, and figuring out why.
        </SectionDesc>

        {instructions}

        {user ? (
          <ProgressBar>
            <ProgressText>{completedCount} OF {totalCount} COMPLETED</ProgressText>
            <ProgressTrack>
              <ProgressFill pct={pct} />
            </ProgressTrack>
            <ProgressText>{pct}%</ProgressText>
          </ProgressBar>
        ) : (
          <AuthBar>
            <AuthLabel>Sign in to track your progress</AuthLabel>
            <AuthBtn onClick={signIn}>SIGN IN WITH GOOGLE</AuthBtn>
          </AuthBar>
        )}

        <FilterRow>
          <FilterLabel>CATEGORY</FilterLabel>
          {categories.map(c => (
            <Pill key={c} active={category === c} onClick={() => setCategory(c)}>
              {c === "cicd" ? "CI/CD" : c === "iac" ? "IaC" : c}
            </Pill>
          ))}
        </FilterRow>
        <FilterRow style={{ marginBottom: "48px" }}>
          <FilterLabel>LEVEL</FilterLabel>
          {difficulties.map(d => (
            <Pill key={d} active={difficulty === d} onClick={() => setDifficulty(d)}>
              {d}
            </Pill>
          ))}
        </FilterRow>

        <Row>
          {filtered.length === 0 && (
            <Col>
              <BottomCTA style={{ marginTop: 0, fontStyle: "normal" }}>
                No challenges match those filters. Try a different combination.
              </BottomCTA>
            </Col>
          )}
          {filtered.map((c) => (
            <Col md="6" lg="4" key={c.id}>
              <ChallengeCard onClick={() => onSelectLab(c)} aria-label={`Open lab: ${c.title}`}>
                <CardHeader>
                  <div>
                    <DiffBadge level={c.difficulty}>{c.difficulty}</DiffBadge>
                    {c.tier === "advanced" && !hasRole(role, "client") && (
                      <LockedTag>Locked · client</LockedTag>
                    )}
                    {completedLabs.includes(c.id) && <CompletedDot title="Completed" />}
                  </div>
                  <TimeBadge>{c.time}</TimeBadge>
                </CardHeader>
                <CardTitle>{c.title}</CardTitle>
                <CardDesc>{c.description}</CardDesc>
                <Tags>
                  {c.tags.map((t, j) => <Tag key={j}>{t}</Tag>)}
                </Tags>
              </ChallengeCard>
            </Col>
          ))}
        </Row>

        <BottomCTA>
          These are the same challenges we give our junior engineers.
          If you can do 10 of them, you should probably talk to us.
        </BottomCTA>
      </Container>
    </SectionStyled>
  );
};

export default Labs;
